import React, {useEffect, useMemo, useRef, useState} from "react";
import axios from "./axiosConfig";
import {
    Button,
    Form,
    Spinner,
    Row,
    Col,
    Modal,
    InputGroup,
    Alert,
    Badge,
} from "react-bootstrap";
import Editor, {OnMount} from "@monaco-editor/react";
import * as yaml from "js-yaml";

interface RuleMeta {
    id: number;
    tenant_id: number;
    name: string;
    file_path: string;
}

interface RuleDetail extends RuleMeta {
    yaml: string;
    organization_ids?: number[];
    metric_type?: string;
}

interface Organization {
    id: number;
    name: string;
}

type Scope = "main" | "modal";

const normalizeIds = (ids: number[]): number[] =>
    [...ids].sort((a, b) => a - b);

const CustomRuleManager: React.FC = () => {
    const [rules, setRules] = useState<RuleMeta[]>([]);
    const [selected, setSelected] = useState<string>("");
    const [content, setContent] = useState<string>("");
    const [origin, setOrigin] = useState<string>("");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Organizations
    const [orgs, setOrgs] = useState<Organization[]>([]);
    const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]); // main area
    const [originalOrgIds, setOriginalOrgIds] = useState<number[]>([]); // snapshot from backend

    // Metric type 下拉选项 + 当前选择（主区 / 弹窗）+ 原始值
    const [metricTypeOptions, setMetricTypeOptions] = useState<string[]>([]);
    const [metricType, setMetricType] = useState<string>(""); // main
    const [originalMetricType, setOriginalMetricType] = useState<string>(""); // snapshot from backend
    const [modalMetricType, setModalMetricType] = useState<string>(""); // modal

    // New Rule (Modal)
    const [showNew, setShowNew] = useState(false);
    const [newName, setNewName] = useState("");
    const [newYaml, setNewYaml] = useState("");
    const [modalOrgIds, setModalOrgIds] = useState<number[]>([]);

    // Alerts
    const [pageAlert, setPageAlert] = useState<{ variant: string; msg: string } | null>(null);
    const [modalAlert, setModalAlert] = useState<{ variant: string; msg: string } | null>(null);

    // Monaco refs
    const mainMonacoRef = useRef<any>(null);
    const mainEditorRef = useRef<any>(null);
    const modalMonacoRef = useRef<any>(null);
    const modalEditorRef = useRef<any>(null);

    // ===== dirty: 只要 YAML / 组织 / metric_type 任一有变化，就允许保存 =====
    const dirty = useMemo(() => {
        if (!selected) return false;

        const yamlDirty = content !== origin;

        const orgDirty =
            JSON.stringify(normalizeIds(selectedOrgIds)) !==
            JSON.stringify(normalizeIds(originalOrgIds));

        const metricDirty =
            (metricType || "") !== (originalMetricType || "");

        return yamlDirty || orgDirty || metricDirty;
    }, [selected, content, origin, selectedOrgIds, originalOrgIds, metricType, originalMetricType]);

    /** ===== Alert helpers ===== */
    const showMainAlert = (variant: "success" | "danger" | "warning", msg: string) => {
        setPageAlert({variant, msg});
    };
    const showModalAlert = (variant: "success" | "danger" | "warning", msg: string) => {
        setModalAlert({variant, msg});
    };

    /** ===== Monaco helpers ===== */
    const getScopeMonaco = (scope: Scope) =>
        scope === "main" ? mainMonacoRef.current : modalMonacoRef.current;
    const getScopeEditor = (scope: Scope) =>
        scope === "main" ? mainEditorRef.current : modalEditorRef.current;

    const clearMarkers = (scope: Scope) => {
        const monaco = getScopeMonaco(scope);
        const editor = getScopeEditor(scope);
        if (!monaco || !editor) return;
        const model = editor.getModel?.();
        if (!model) return;
        monaco.editor.setModelMarkers(model, "yaml-check", []);
    };

    const setYamlErrorMarker = (err: any, scope: Scope) => {
        const monaco = getScopeMonaco(scope);
        const editor = getScopeEditor(scope);
        if (!monaco || !editor) return;

        const model = editor.getModel?.();
        if (!model) return;

        const line = (err?.mark?.line ?? 0) + 1;
        const column = (err?.mark?.column ?? 0) + 1;

        monaco.editor.setModelMarkers(model, "yaml-check", [
            {
                startLineNumber: line,
                startColumn: Math.max(column, 1),
                endLineNumber: line,
                endColumn: Math.max(column + 1, 1),
                message: String(err?.message || "YAML syntax error"),
                severity: monaco.MarkerSeverity.Error,
            },
        ]);

        editor.revealPositionInCenter({lineNumber: line, column});
        editor.setPosition({lineNumber: line, column});
    };

    /** ===== YAML validation ===== */
    const validateYaml = (text: string, actionLabel: string, scope: Scope) => {
        try {
            yaml.loadAll(text || "");
            clearMarkers(scope);
            if (scope === "main") setPageAlert(null);
            if (scope === "modal") setModalAlert(null);
            return true;
        } catch (e: any) {
            const pos =
                e?.mark && typeof e.mark.line === "number"
                    ? ` (line ${e.mark.line + 1}, col ${e.mark.column + 1})`
                    : "";
            setYamlErrorMarker(e, scope);
            if (scope === "main")
                showMainAlert("danger", `${actionLabel} failed: YAML syntax error${pos}`);
            if (scope === "modal")
                showModalAlert("danger", `${actionLabel} failed: YAML syntax error${pos}`);
            return false;
        }
    };

    /** ===== Data loading ===== */
    const loadRuleList = async () => {
        try {
            setLoading(true);
            const res = await axios.get("/api/prometheus/custom-rules");
            setRules(res.data.rules || []);
        } catch {
            showMainAlert("danger", "Failed to load rule list");
        } finally {
            setLoading(false);
        }
    };

    const loadRuleDetail = async (name: string) => {
        if (!name) return;
        try {
            setLoading(true);
            const res = await axios.get<RuleDetail>(`/api/prometheus/custom-rule/${name}`);
            const yml = res.data.yaml || "";
            setContent(yml);
            setOrigin(yml);

            const orgIds = res.data.organization_ids || [];
            setSelectedOrgIds(orgIds);
            setOriginalOrgIds(orgIds);

            const mtype = res.data.metric_type || "";
            setMetricType(mtype);
            setOriginalMetricType(mtype);

            clearMarkers("main");
            showMainAlert("success", `Loaded: ${name}`);
        } catch {
            showMainAlert("danger", "Failed to load rule content");
        } finally {
            setLoading(false);
        }
    };

    const loadOrganizations = async () => {
        try {
            const res = await axios.get("/api/organizations");
            const list: Organization[] = res.data?.organizations || res.data || [];
            setOrgs(list);
        } catch {
            showMainAlert("danger", "Failed to load organizations");
        }
    };

    const loadMetricTypes = async () => {
        try {
            const res = await axios.get("/api/value-metric-types");
            const list: string[] = res.data?.metric_types || res.data || [];
            setMetricTypeOptions(list);
        } catch {
            showMainAlert(
                "warning",
                "Failed to load metric types. Metric type select may be empty."
            );
        }
    };

    /** ===== Save/Delete/Create ===== */
    const handleSave = async () => {
        if (!selected) return showMainAlert("danger", "Please select a rule first");
        if (!validateYaml(content, "Save", "main")) return;
        if (!selectedOrgIds.length)
            return showMainAlert(
                "warning",
                "Please select at least one organization"
            );

        try {
            setSaving(true);
            const res = await axios.post("/api/prometheus/custom-rule", {
                name: selected,
                yaml: content,
                organization_ids: selectedOrgIds,
                metric_type: metricType || undefined,
            });
            setOrigin(content);
            setOriginalOrgIds(selectedOrgIds);
            setOriginalMetricType(metricType || "");
            showMainAlert("success", res?.data?.message || "Rule saved successfully");
            await loadRuleList();
        } catch {
            showMainAlert("danger", "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selected)
            return showMainAlert("danger", "Please select a rule first");
        if (!window.confirm(`Are you sure to delete rule "${selected}"?`)) return;
        try {
            setDeleting(true);
            const res = await axios.delete(`/api/prometheus/custom-rule/${selected}`);
            showMainAlert("success", res?.data?.message || "Rule deleted");
            setSelected("");
            setContent("");
            setOrigin("");
            setSelectedOrgIds([]);
            setOriginalOrgIds([]);
            setMetricType("");
            setOriginalMetricType("");
            clearMarkers("main");
            await loadRuleList();
        } catch {
            showMainAlert("danger", "Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    const handleCreateNew = async () => {
        if (!newName.trim() || !newYaml.trim())
            return showModalAlert(
                "danger",
                "Please provide both name and YAML content"
            );
        if (!modalOrgIds.length)
            return showModalAlert(
                "warning",
                "Please select at least one organization"
            );
        if (!validateYaml(newYaml, "Create", "modal")) return;

        try {
            setSaving(true);
            const res = await axios.post("/api/prometheus/custom-rule", {
                name: newName.trim(),
                yaml: newYaml,
                organization_ids: modalOrgIds,
                metric_type: modalMetricType || undefined,
            });
            showModalAlert("success", res?.data?.message || "New rule created");
            setShowNew(false);
            setSelected(newName.trim());
            setContent(newYaml);
            setOrigin(newYaml);

            setSelectedOrgIds(modalOrgIds);
            setOriginalOrgIds(modalOrgIds);

            setMetricType(modalMetricType || "");
            setOriginalMetricType(modalMetricType || "");

            setNewName("");
            setNewYaml("");
            setModalOrgIds([]);
            setModalMetricType("");

            clearMarkers("main");
            await loadRuleList();
            setModalAlert(null);
            clearMarkers("modal");
        } catch (err: any) {
            const message =
                err?.response?.data?.error || err?.message || "Create failed";

            let userTip = "";
            if (/invalid rule name/i.test(message)) {
                userTip =
                    "Invalid rule name: Only letters, numbers, hyphens (-), and underscores (_) are allowed. " +
                    "Spaces or special characters are not permitted. Example: cpu_usage_high";
            } else if (/exists/i.test(message)) {
                userTip = "The rule name already exists. Please use a different name.";
            } else if (/yaml/i.test(message)) {
                userTip =
                    "YAML syntax error: Please check indentation, colons (:), and list dashes (-). Example: key: value";
            } else if (/timeout|network/i.test(message)) {
                userTip =
                    "Network or server connection error. Please try again later.";
            } else {
                userTip = "Please verify your input or contact the system administrator.";
            }

            const finalMsg = `${message}. ${userTip}`;
            showModalAlert("danger", finalMsg);
        } finally {
            setSaving(false);
        }
    };

    /** ===== onMount ===== */
    const onMainEditorMount: OnMount = (editor, monaco) => {
        mainEditorRef.current = editor;
        mainMonacoRef.current = monaco;
    };
    const onModalEditorMount: OnMount = (editor, monaco) => {
        modalEditorRef.current = editor;
        modalMonacoRef.current = monaco;
    };

    /** ===== Effects ===== */
    useEffect(() => {
        loadOrganizations();
        loadMetricTypes();
        loadRuleList();
    }, []);

    useEffect(() => {
        if (selected) loadRuleDetail(selected);
    }, [selected]);

    // 供 MetricConfigForm 用的跨组件事件（只传 name + yaml 即可）
    useEffect(() => {
        const openFromOutside = (e: any) => {
            const {name, yaml: yamlContent} = e.detail || {};
            setNewName(name || "");
            setNewYaml(yamlContent || "");
            setModalOrgIds([]);
            setModalMetricType("");
            setModalAlert(null);
            clearMarkers("modal");
            setShowNew(true);
        };
        window.addEventListener("custom-rule:open-new", openFromOutside);
        return () =>
            window.removeEventListener("custom-rule:open-new", openFromOutside);
    }, []);

    /** ===== Render helpers ===== */
    const renderOrgMultiSelect = (
        value: number[],
        onChange: (ids: number[]) => void,
        controlId: string,
        placeholder = "Select organizations"
    ) => (
        <Form.Group controlId={controlId} className="mb-2">
            <Form.Label>Organizations</Form.Label>
            <Form.Select
                multiple
                value={value.map(String)}
                onChange={(e) => {
                    const options = Array.from(e.target.selectedOptions).map((o) =>
                        Number(o.value)
                    );
                    onChange(options);
                }}
            >
                {orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                        {o.name}
                    </option>
                ))}
            </Form.Select>
            <div className="mt-2" aria-live="polite">
                {value.length ? (
                    value
                        .map((id) => orgs.find((o) => o.id === id)?.name || String(id))
                        .map((name, idx) => (
                            <Badge bg="secondary" key={`${name}-${idx}`} className="me-1">
                                {name}
                            </Badge>
                        ))
                ) : (
                    <small className="text-muted">{placeholder}</small>
                )}
            </div>
        </Form.Group>
    );

    const renderMetricTypeSelect = (
        value: string,
        onChange: (val: string) => void,
        controlId: string
    ) => (
        <Form.Group controlId={controlId} className="mb-2">
            <Form.Label>Metric Type (for value-mapped status only)</Form.Label>
            <Form.Select
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <option value="">
                    -- None (use this for CPU / memory / bandwidth, etc.) --
                </option>
                {metricTypeOptions.map((mt) => (
                    <option key={mt} value={mt}>
                        {mt}
                    </option>
                ))}
            </Form.Select>
            <Form.Text>
                Loaded from value mappings (metric_type column). Only select a metric type
                if this rule uses value mapping (for example: BGP or VPN state). For
                normal numeric metrics such as CPU, memory, or bandwidth thresholds,
                please leave this field empty.
            </Form.Text>
        </Form.Group>
    );

    return (
        <div className="container mt-4">
            <h4 className="mb-3">Custom Prometheus Rules</h4>

            {/* Top controls: rule selector + New Rule button */}
            <Row className="mb-3 align-items-center">
                <Col sm={6}>
                    <InputGroup>
                        <Form.Select
                            value={selected}
                            onChange={(e) => setSelected(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">-- Select a rule --</option>
                            {rules.map((r) => (
                                <option key={r.id} value={r.name}>
                                    {r.name}
                                </option>
                            ))}
                        </Form.Select>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setModalAlert(null);
                                clearMarkers("modal");
                                setNewName("");
                                setNewYaml("");
                                setModalOrgIds([]);
                                setModalMetricType("");
                                setShowNew(true);
                            }}
                        >
                            + New Rule
                        </Button>
                    </InputGroup>
                </Col>

                <Col sm={6} className="text-end">
                    <Button
                        variant="danger"
                        className="me-2"
                        disabled={!selected || deleting}
                        onClick={handleDelete}
                    >
                        {deleting ? <Spinner size="sm" animation="border"/> : "Delete"}
                    </Button>
                    <Button
                        variant="success"
                        disabled={!selected || saving || !dirty}
                        onClick={handleSave}
                        title={!dirty && selected ? "No changes to save" : ""}
                    >
                        {saving ? <Spinner size="sm" animation="border"/> : "Save Changes"}
                    </Button>
                </Col>
            </Row>

            {/* Main area: Metric Type + Organizations */}
            <Row className="mb-3">
                <Col md={4}>
                    {renderMetricTypeSelect(metricType, setMetricType, "metric-type-main")}
                </Col>
                <Col md={8}>
                    {renderOrgMultiSelect(
                        selectedOrgIds,
                        setSelectedOrgIds,
                        "orgs-main",
                        "No organization selected"
                    )}
                </Col>
            </Row>

            {/* Page alert (load/save/delete) */}
            {pageAlert && (
                <Alert
                    variant={pageAlert.variant}
                    dismissible
                    onClose={() => setPageAlert(null)}
                    className="mb-3"
                >
                    {pageAlert.msg}
                </Alert>
            )}

            {/* Editor */}
            {loading ? (
                <div className="text-center mt-4">
                    <Spinner animation="border"/> Loading...
                </div>
            ) : (
                <Editor
                    height="64vh"
                    defaultLanguage="yaml"
                    theme="vs-dark"
                    value={content}
                    onMount={onMainEditorMount}
                    onChange={(v) => setContent(v || "")}
                    options={{
                        readOnly: !selected,
                        fontSize: 14,
                        lineNumbers: "on",
                        minimap: {enabled: false},
                        scrollBeyondLastLine: false,
                    }}
                />
            )}

            {/* Create modal */}
            <Modal
                show={showNew}
                onHide={() => setShowNew(false)}
                size="xl"
                dialogClassName="custom-rule-modal"
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Create New Rule</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalAlert && (
                        <Alert
                            variant={modalAlert.variant}
                            dismissible
                            onClose={() => setModalAlert(null)}
                            className="mb-3"
                        >
                            {modalAlert.msg}
                        </Alert>
                    )}

                    <Row className="mb-3">
                        <Col md={4}>
                            {renderMetricTypeSelect(
                                modalMetricType,
                                setModalMetricType,
                                "metric-type-modal"
                            )}
                        </Col>
                        <Col md={8}>
                            {renderOrgMultiSelect(
                                modalOrgIds,
                                setModalOrgIds,
                                "orgs-modal",
                                "Please select at least one organization"
                            )}
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Rule Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter rule name (e.g. custom_rules)"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>YAML Content</Form.Label>
                        <Editor
                            height="50vh"
                            defaultLanguage="yaml"
                            theme="vs-dark"
                            value={newYaml}
                            onMount={onModalEditorMount}
                            onChange={(v) => setNewYaml(v || "")}
                            options={{
                                fontSize: 14,
                                minimap: {enabled: false},
                                scrollBeyondLastLine: false,
                            }}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowNew(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreateNew} disabled={saving}>
                        {saving ? "Creating..." : "Create"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CustomRuleManager;
